import Chip from "@mui/material/Chip";
import { styled } from "@mui/material/styles";
import Paper from "@mui/material/Paper";
import clsx from "clsx";
import { Query, SearchPillData } from "../interfaces";

const PREFIX = "SearchPills";

const classes = {
  root: `${PREFIX}-root`,
  pillStyle: `${PREFIX}-pillStyle`,
  errorPillColor: `${PREFIX}-errorPillColor`,
  secondaryPillColor: `${PREFIX}-secondaryPillColor`,
  standardColor: `${PREFIX}-standardColor`,
  divider: `${PREFIX}-divider`,
};

const StyledPaper = styled(Paper)(({ theme }) => ({
  [`&.${classes.root}`]: {
    display: "flex",
    justifyContent: "flex-start",
    flexWrap: "wrap",
    listStyle: "none",
    padding: 0,
    margin: 0,
    boxShadow: "none",
  },

  [`& .${classes.pillStyle}`]: {
    "backgroundColor": theme.palette.secondary.main,
    "border": "1px solid #acc8df",
    "marginTop": ".25rem",
    "marginBottom": ".25rem",
    "whiteSpace": "break-spaces",
    "marginRight": ".5rem",
    "fontWeight": 400,
    "fontStyle": "normal",
    "fontSize": 12,
    "color": theme.palette.primary.main,
    "&:focus": {
      "& .MuiSvgIcon-root": {
        color: "white",
        backgroundColor: theme.palette.primary.main,
        border: "solid 1",
        borderRadius: ".625rem",
      },
    },
    "&.MuiChip-root": {
      height: 20,
      width: "flex",
    },
    "& .MuiChip-label": {
      paddingLeft: 8,
      whiteSpace: "break-spaces",
    },
    "& .MuiSvgIcon-root": {
      "marginRight": 0,
      "color": theme.palette.primary.main,
      "&:hover": {
        color: "white",
        backgroundColor: theme.palette.primary.main,
        border: "solid 1",
        borderRadius: ".625rem",
      },
    },
  },

  [`& .${classes.errorPillColor}`]: {
    "borderColor": theme.palette.error.dark,
    "backgroundColor": "#f9e5e5",
    "color": theme.palette.error.dark,
    "&:hover": {
      backgroundColor: "#f9e5e5",
    },
    "&:focus": {
      "backgroundColor": "#f9e5e5",
      "& .MuiSvgIcon-root": {
        color: "white",
        backgroundColor: theme.palette.error.dark,
        border: "solid 1",
        borderRadius: ".625rem",
      },
    },
    "& .MuiSvgIcon-root": {
      "color": theme.palette.error.dark,
      "&:hover": {
        color: "white",
        backgroundColor: theme.palette.error.dark,
        border: "solid 1",
        borderRadius: ".625rem",
      },
    },
  },

  [`& .${classes.secondaryPillColor}`]: {
    borderColor: "#00908C",
    backgroundColor: "#E6FFFF",
  },

  [`& .${classes.standardColor}`]: {
    borderColor: "#70643e",
    backgroundColor: "#ede7d3",
  },

  [`& .${classes.divider}`]: {
    margin: "-1rem 0 1rem 0",
  },
}));

interface Props {
  dataArray: SearchPillData[];
  deleteSearchPill: (
    pill: SearchPillData,
    dataArray: SearchPillData[],
    appState?: Query,
  ) => void;
  standardColor?: boolean;
  secondaryColor?: boolean;
  errorColor?: boolean;
  appState?: Query;
}

export default function SearchPills({
  dataArray,
  deleteSearchPill,
  standardColor,
  secondaryColor,
  errorColor,
  appState,
}: Props) {
  return (
    <StyledPaper className={clsx(classes.root, "searchPillsContainer")}>
      {(dataArray ?? []).map((data: SearchPillData, index: number) => {
        const dataVal = data.value
          ? Array.isArray(data.value)
            ? data.value.join(" ").toLowerCase().replace(/ /g, "-")
            : data.value.toLowerCase().replace(/ /g, "-")
          : "";
        const duplicateLabel =
          dataArray
            .map((val: any) => val.label)
            .filter((val) => val === data.label).length > 1;

        const searchPillId = data.label
          ? `${data.label
              .toLowerCase()
              .replace(/[^A-Z0-9]/gi, "-")
              .replace(/[-]+/g, "-")}-search-pills${
              duplicateLabel && dataVal ? `-${dataVal}` : ""
            }`
          : `${dataVal}-search-pills`;
        return data.value ? (
          <li key={index}>
            <Chip
              label={
                data.label
                  ? `${data.label}: ${
                      Array.isArray(data.value)
                        ? data.value.join(", ")
                        : data.value
                    }`
                  : data.value
              }
              onDelete={
                data.static
                  ? undefined
                  : () => deleteSearchPill(data, dataArray, appState)
              }
              sx={{
                "& .MuiChip-deleteIcon": {
                  fontSize: "1.25rem",
                },
              }}
              className={clsx(
                classes.pillStyle,
                standardColor && classes.standardColor,
                secondaryColor && classes.secondaryPillColor,
                errorColor && classes.errorPillColor,
              )}
              id={searchPillId}
            />
          </li>
        ) : (
          ""
        );
      })}
    </StyledPaper>
  );
}
